import { Controller } from "@hotwired/stimulus"

// Read-along voice playback (ADR-0097, generalized ADR-0098): fetches
// lazily-generated, server-cached audio + word-level alignment (ElevenLabs
// timestamps, grouped into words/paragraph-breaks by
// ElevenLabsClient.synthesize_with_alignment), builds a per-word span
// rendering of the paired text on first play, then keeps the current word
// highlighted in sync with playback. Clicking any word seeks there and
// plays. The button is a genuine play/pause toggle (native Audio
// pause/resume, not stop-and-reset) -- word-click already covers "start
// over from anywhere", including the beginning.
//
// Generic on purpose: first built for session recap narration, reused as-is
// for moment dialogue (NPC lines) via ADR-0098, which also retired the
// separate ADR-0096 voice-play primitive -- once every real call site needed
// word-sync, there was no remaining case for the simpler stop/reset button.
// Any future "click to hear this text read aloud, with highlighting" surface
// should use this controller too, rather than a new copy of the same logic.
//
// This controller owns its own Audio instance rather than coordinating with
// other read-along instances on the same page (no page-wide "only one clip
// at a time" guarantee) -- acceptable since the Feed and Recap tab aren't
// normally visible together; unifying that is a reasonable future addition
// if it ever proves to matter in practice.
export default class extends Controller {
  static values = { url: String }
  static targets = ["button", "playIcon", "pauseIcon", "text"]

  connect() {
    this.audio = null
    this.words = null
    this.activeWord = null
  }

  disconnect() {
    this.audio?.pause()
  }

  async toggle() {
    // Already fetched once this page load -- just play/pause, no re-fetch.
    if (this.audio) {
      if (this.audio.paused) {
        this.audio.play()
      } else {
        this.audio.pause()
      }
      return
    }

    if (this.hasButtonTarget) this.buttonTarget.disabled = true

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        }
      })
      const data = await response.json()

      if (!response.ok || !data.audio_url) {
        console.warn("[read-along]", data.error || "Voice playback failed")
        return
      }

      this.buildWordSpans(data.words || [])

      this.audio = new Audio(data.audio_url)
      this.audio.addEventListener("play", () => this.setPlayingState(true))
      this.audio.addEventListener("pause", () => this.setPlayingState(false))
      this.audio.addEventListener("ended", () => this.setPlayingState(false))
      this.audio.addEventListener("timeupdate", () => this.highlightCurrentWord())
      this.audio.play()
    } finally {
      if (this.hasButtonTarget) this.buttonTarget.disabled = false
    }
  }

  // Replaces the plain rendered text with a per-word span build straight
  // from the alignment array -- each word keeps a direct reference to its
  // <span> (word.el) for highlight/lookup, rather than re-querying the DOM
  // on every timeupdate tick.
  buildWordSpans(tokens) {
    this.words = tokens.filter(t => t.type === "word")

    const container = this.hasTextTarget ? this.textTarget : this.element
    container.innerHTML = ""

    let paragraph = document.createElement("p")
    container.appendChild(paragraph)

    tokens.forEach(token => {
      if (token.type === "break") {
        paragraph = document.createElement("p")
        container.appendChild(paragraph)
        return
      }

      const span = document.createElement("span")
      span.textContent = token.text
      span.className = "cursor-pointer hover:text-tavern-gold transition-colors rounded px-0.5 -mx-0.5"
      span.addEventListener("click", () => this.seekTo(token))
      paragraph.appendChild(span)
      paragraph.appendChild(document.createTextNode(" "))
      token.el = span
    })
  }

  seekTo(word) {
    if (!this.audio) return
    this.audio.currentTime = word.start
    this.audio.play()
  }

  highlightCurrentWord() {
    if (!this.words || !this.audio) return

    const t = this.audio.currentTime
    let current = null
    for (const word of this.words) {
      if (word.start <= t) current = word
      else break
    }
    if (current === this.activeWord) return

    this.activeWord?.el?.classList.remove("bg-tavern-gold/20", "text-tavern-gold")
    this.activeWord = current
    this.activeWord?.el?.classList.add("bg-tavern-gold/20", "text-tavern-gold")
  }

  setPlayingState(isPlaying) {
    if (this.hasPlayIconTarget) this.playIconTarget.classList.toggle("hidden", isPlaying)
    if (this.hasPauseIconTarget) this.pauseIconTarget.classList.toggle("hidden", !isPlaying)
  }
}
